import { useEffect, useState } from "react";
import api, { rupiah, getErrorMessage } from "@/lib/api";
import { moneyOf } from "@/lib/exactMoney";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Archive, Trash2, GitMerge, Upload, Download, FileDown } from "lucide-react";
import { RecordDialog } from "@/pages/modules/forms";
import { AlertBanner, StatusBadge } from "@/components/ui/design";

const FS_LINES = ["Kas & Setara Kas", "Piutang Usaha", "Persediaan", "Aset Tetap", "Aset Lain",
  "Utang Usaha", "Utang Lain", "Pinjaman", "Ekuitas", "Pendapatan", "Beban Pokok", "Beban Usaha", "Beban Lain"];
const AREAS = ["Kas & Bank", "Piutang", "Persediaan", "Aset Tetap", "Utang", "Pinjaman", "Ekuitas", "Pendapatan", "Beban"];

export default function Mapping({ id }) {
  const [data, setData] = useState({ accounts: [], issues: {} });
  const [meta, setMeta] = useState(null);
  const [impact, setImpact] = useState(null);
  const [dlg, setDlg] = useState(null);      // "coa" (add/edit)
  const [edit, setEdit] = useState(null);
  const [merge, setMerge] = useState(null);
  const [imp, setImp] = useState(false);
  const [flt, setFlt] = useState({ status: "all", map: "all", type: "all", fs: "all" });

  const load = () => {
    api.get(`/engagements/${id}/accounts`).then((r) => setData(r.data));
    api.get(`/engagements/${id}/coa/impact`).then((r) => setImpact(r.data));
  };
  useEffect(() => { load(); api.get("/coa/meta").then((r) => setMeta(r.data)); }, [id]); // eslint-disable-line
  if (!meta) return <div className="text-sm text-muted-foreground p-4">Memuat...</div>;

  const can = meta.can_maintain;
  const map = async (aid, field, value) => {
    const acc = data.accounts.find((a) => a.id === aid);
    await api.put(`/engagements/${id}/accounts/${aid}/mapping`, { fs_line: field === "fs_line" ? value : acc.fs_line, audit_area: field === "audit_area" ? value : acc.audit_area });
    load();
  };
  const saveCoa = async (v) => {
    try {
      if (edit?.id) { const r = await api.put(`/coa/${edit.id}`, v); if (r.data.mapping_impact) toast.warning(r.data.impact_warning); }
      else await api.post(`/engagements/${id}/coa`, v);
      setDlg(null); load(); toast.success("COA disimpan");
    } catch (e) { toast.error(getErrorMessage(e)); }
  };
  const archive = async (a) => { if (!window.confirm(`Arsipkan akun ${a.account_code}?`)) return; try { await api.post(`/coa/${a.id}/archive`); load(); toast.success("Diarsipkan"); } catch (e) { toast.error(getErrorMessage(e)); } };
  const del = async (a) => {
    try {
      const ref = await api.get(`/coa/${a.id}/references`);
      if (!ref.data.can_hard_delete) return toast.error(`Tidak bisa hapus permanen — direferensikan: ${Object.keys(ref.data.references).join(", ")}. Gunakan Arsipkan.`);
      if (!window.confirm(`Hapus PERMANEN akun ${a.account_code}? Tidak dapat dibatalkan.`)) return;
      await api.delete(`/coa/${a.id}`); load(); toast.success("Dihapus permanen");
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const coaFields = [
    { key: "account_code", label: "Kode Akun", type: "text", required: true },
    { key: "account_name", label: "Nama Akun", type: "text", required: true },
    { key: "account_type", label: "Tipe Akun", type: "select", options: meta.account_types },
    { key: "normal_balance", label: "Saldo Normal", type: "select", options: meta.normal_balances },
    { key: "parent_account", label: "Akun Induk", type: "text" },
    { key: "fs_line", label: "FS Line", type: "select", options: meta.fs_lines },
    { key: "audit_area", label: "Area Audit", type: "select", options: meta.areas },
    { key: "classification", label: "Lancar / Tidak Lancar", type: "select", options: meta.classifications },
    { key: "tax_category", label: "Kategori Pajak", type: "select", options: meta.tax_categories },
    { key: "effective_from", label: "Berlaku Dari", type: "date" },
    { key: "effective_to", label: "Berlaku Sampai", type: "date" },
    { key: "active", label: "Aktif", type: "checkbox", checkboxLabel: "Akun Aktif" },
    { key: "reason", label: "Alasan Perubahan (untuk edit)", type: "textarea", showIf: () => !!edit?.id },
    { key: "notes", label: "Catatan", type: "textarea" },
  ];

  const rows = data.accounts.filter((a) => {
    const act = a.active !== false;
    if (flt.status === "active" && !act) return false;
    if (flt.status === "inactive" && act) return false;
    if (flt.map === "mapped" && !a.fs_line) return false;
    if (flt.map === "unmapped" && a.fs_line) return false;
    if (flt.type !== "all" && a.account_type !== flt.type) return false;
    if (flt.fs !== "all" && a.fs_line !== flt.fs) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      {/* toolbar */}
      <div className="section-card p-3 flex flex-wrap items-center gap-2" data-testid="coa-toolbar">
        <span className="font-head font-semibold text-sm mr-2">Manajemen COA</span>
        <Button size="sm" disabled={!can} onClick={() => { setEdit(null); setDlg("coa"); }} data-testid="coa-add"><Plus size={13} className="mr-1" />Tambah COA</Button>
        <Button size="sm" variant="outline" disabled={!can} onClick={() => setImp(true)} data-testid="coa-import"><Upload size={13} className="mr-1" />Import</Button>
        <Button size="sm" variant="outline" onClick={() => window.open(`${api.defaults.baseURL}/engagements/${id}/coa/export`, "_blank")} data-testid="coa-export"><Download size={13} className="mr-1" />Export</Button>
        <Button size="sm" variant="ghost" onClick={() => window.open(`${api.defaults.baseURL}/coa/template.xlsx`, "_blank")} data-testid="coa-tpl-xlsx"><FileDown size={13} className="mr-1" />Template .xlsx</Button>
        <Button size="sm" variant="ghost" onClick={() => window.open(`${api.defaults.baseURL}/coa/template.csv`, "_blank")} data-testid="coa-tpl-csv">.csv</Button>
        {!can && <span className="text-[11px] text-amber-700">Hanya Admin/Manager/Partner dapat mengubah COA.</span>}
      </div>

      {/* filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <Flt label="Status" v={flt.status} set={(v) => setFlt({ ...flt, status: v })} opts={[["all", "Semua"], ["active", "Aktif"], ["inactive", "Nonaktif"]]} tid="coa-flt-status" />
        <Flt label="Mapping" v={flt.map} set={(v) => setFlt({ ...flt, map: v })} opts={[["all", "Semua"], ["mapped", "Termapping"], ["unmapped", "Belum"]]} tid="coa-flt-map" />
        <Flt label="Tipe" v={flt.type} set={(v) => setFlt({ ...flt, type: v })} opts={[["all", "Semua Tipe"], ...meta.account_types.map((t) => [t, t])]} />
        <Flt label="FS Line" v={flt.fs} set={(v) => setFlt({ ...flt, fs: v })} opts={[["all", "Semua FS"], ...meta.fs_lines.map((t) => [t, t])]} />
        <span className="text-xs text-muted-foreground">{rows.length} akun</span>
      </div>

      {impact?.review_required && (
        <AlertBanner tone="warning" title="COA / MAPPING REVIEW REQUIRED" testid="coa-impact">
          {Object.entries(impact.issues).filter(([, v]) => v.length).map(([k, v]) => `${k}: ${v.length}`).join(" · ")}
        </AlertBanner>
      )}

      <div className="section-card overflow-x-auto">
        <div className="px-4 py-2 border-b border-border font-head font-semibold text-sm">Chart of Accounts / TB — Pemetaan FS & Area Audit</div>
        <table className="w-full audit-table">
          <thead><tr><th>Kode</th><th>Nama Akun</th><th>Tipe</th><th className="text-right">Saldo</th><th>FS Line</th><th>Area Audit</th><th>Status</th><th className="text-right">Aksi</th></tr></thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id} className={a.active === false ? "opacity-60" : !a.fs_line ? "bg-amber-50/40" : ""} data-testid={`acc-row-${a.id}`}>
                <td className="font-mono text-xs">{a.account_code}</td>
                <td className="font-medium">{a.account_name}{a.merged_into && <span className="text-[10px] text-muted-foreground ml-1">→ {a.merged_into}</span>}</td>
                <td className="text-xs">{a.account_type || "—"}</td>
                <td className="text-right tabular">{moneyOf(a.current_balance_exact, a.current_balance, rupiah)}</td>
                <td>
                  <Select value={a.fs_line || "__none"} onValueChange={(v) => map(a.id, "fs_line", v === "__none" ? "" : v)}>
                    <SelectTrigger className="h-7 text-xs w-40" data-testid={`fs-${a.id}`}><SelectValue placeholder="Pilih" /></SelectTrigger>
                    <SelectContent><SelectItem value="__none">—</SelectItem>{FS_LINES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td>
                  <Select value={a.audit_area || "__none"} onValueChange={(v) => map(a.id, "audit_area", v === "__none" ? "" : v)}>
                    <SelectTrigger className="h-7 text-xs w-36"><SelectValue placeholder="Pilih" /></SelectTrigger>
                    <SelectContent><SelectItem value="__none">—</SelectItem>{AREAS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td>{a.active === false ? <StatusBadge tone="neutral" icon={false}>Arsip</StatusBadge> : <StatusBadge tone="success" icon={false}>Aktif</StatusBadge>}</td>
                <td className="whitespace-nowrap text-right">
                  <Button variant="ghost" size="sm" className="h-6 px-1" disabled={!can} title="Edit" onClick={() => { setEdit(a); setDlg("coa"); }} data-testid={`coa-edit-${a.id}`}><Pencil size={13} /></Button>
                  <Button variant="ghost" size="sm" className="h-6 px-1" disabled={!can || a.active === false} title="Arsipkan" onClick={() => archive(a)} data-testid={`coa-archive-${a.id}`}><Archive size={13} /></Button>
                  <Button variant="ghost" size="sm" className="h-6 px-1" disabled={!can} title="Merge/Replace" onClick={() => setMerge(a)} data-testid={`coa-merge-${a.id}`}><GitMerge size={13} /></Button>
                  <Button variant="ghost" size="sm" className="h-6 px-1 text-destructive" disabled={!can} title="Hapus" onClick={() => del(a)} data-testid={`coa-del-${a.id}`}><Trash2 size={13} /></Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="text-center text-muted-foreground py-6">Belum ada akun. Tambah COA, Import, atau bangun dari TB di Data Intake.</td></tr>}
          </tbody>
        </table>
      </div>

      <RecordDialog open={dlg === "coa"} onOpenChange={(o) => !o && setDlg(null)} title={edit?.id ? `Edit COA — ${edit.account_code}` : "Tambah COA"}
        description={edit?.id ? "Perubahan FS/Area/Tipe/Status memicu peringatan reviu mapping." : "Tambah akun baru ke COA perikatan ini."}
        fields={coaFields} initial={edit || { active: true }} onSubmit={saveCoa} testid="coa-dialog" />

      {merge && <MergeDialog eng={id} acc={merge} accounts={data.accounts} onClose={() => setMerge(null)} onDone={() => { setMerge(null); load(); }} />}
      {imp && <ImportDialog eng={id} onClose={() => setImp(false)} onDone={() => { setImp(false); load(); }} />}
    </div>
  );
}

const Flt = ({ label, v, set, opts, tid }) => (
  <div className="flex items-center gap-1">
    <Label className="text-[11px] text-muted-foreground">{label}</Label>
    <Select value={v} onValueChange={set}><SelectTrigger className="h-7 text-xs w-36" data-testid={tid}><SelectValue /></SelectTrigger>
      <SelectContent>{opts.map(([val, lbl]) => <SelectItem key={val} value={val}>{lbl}</SelectItem>)}</SelectContent></Select>
  </div>
);

function MergeDialog({ eng, acc, accounts, onClose, onDone }) {
  const [rep, setRep] = useState("");
  const [reason, setReason] = useState("");
  const submit = async () => {
    if (!rep || !reason) return toast.error("Pengganti & alasan wajib diisi");
    try { const r = await api.post(`/coa/${acc.id}/merge`, { replacement_id: rep, reason }); toast.success(`Digabung ke ${r.data.replacement}`); onDone(); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="coa-merge-dialog">
        <DialogHeader><DialogTitle>Merge / Replace Akun</DialogTitle>
          <DialogDescription>Ganti {acc.account_code} — {acc.account_name} dengan akun lain; saldo dipindahkan & lineage dipertahankan.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Akun Pengganti</Label>
            <Select value={rep} onValueChange={setRep}><SelectTrigger data-testid="merge-replacement"><SelectValue placeholder="Pilih akun pengganti" /></SelectTrigger>
              <SelectContent>{accounts.filter((a) => a.id !== acc.id && a.active !== false).map((a) => <SelectItem key={a.id} value={a.id}>{a.account_code} — {a.account_name}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label className="text-xs">Alasan</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="merge-reason" /></div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="merge-submit">Merge / Replace</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportDialog({ eng, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const analyze = async (file) => {
    if (!file) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const r = await api.post(`/engagements/${eng}/coa/import/analyze`, fd); setPreview(r.data); }
    catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };
  const commit = async () => {
    setBusy(true);
    try { const r = await api.post(`/engagements/${eng}/coa/import/commit`, { rows: preview.rows, source_filename: preview.source_filename });
      toast.success(`Impor: ${r.data.imported} berhasil, ${r.data.rejected} ditolak, ${r.data.warnings} peringatan`); onDone(); }
    catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="coa-import-dialog">
        <DialogHeader><DialogTitle>Import COA (.xlsx / .csv)</DialogTitle>
          <DialogDescription>Deteksi header otomatis, pratinjau, dan validasi duplikat/wajib sebelum impor.</DialogDescription></DialogHeader>
        {!preview ? (
          <div className="py-4"><input type="file" accept=".xlsx,.csv" onChange={(e) => analyze(e.target.files?.[0])} data-testid="coa-import-file" className="text-sm" />{busy && <div className="text-xs text-muted-foreground mt-2">Menganalisis...</div>}</div>
        ) : (
          <div className="space-y-2">
            <div className="flex gap-2 text-xs" data-testid="import-counts">
              <StatusBadge tone="success">OK: {preview.counts.ok}</StatusBadge>
              <StatusBadge tone="warning">Peringatan: {preview.counts.warning}</StatusBadge>
              <StatusBadge tone="danger">Ditolak: {preview.counts.rejected}</StatusBadge>
              <span className="text-muted-foreground self-center">Sumber: {preview.source_filename}</span>
            </div>
            <div className="max-h-72 overflow-y-auto section-card">
              <table className="w-full audit-table"><thead><tr><th>Kode</th><th>Nama</th><th>Tipe</th><th>Status</th><th>Pesan</th></tr></thead>
                <tbody>{preview.rows.map((r, i) => (
                  <tr key={i} data-testid={`import-row-${i}`}>
                    <td className="font-mono text-xs">{r.data.account_code}</td><td className="text-xs">{r.data.account_name}</td><td className="text-xs">{r.data.account_type}</td>
                    <td><StatusBadge tone={r.status === "ok" ? "success" : r.status === "warning" ? "warning" : "danger"} icon={false}>{r.status}</StatusBadge></td>
                    <td className="text-[11px] text-red-600">{[...r.errors, ...r.warnings].join("; ")}</td>
                  </tr>))}</tbody>
              </table>
            </div>
          </div>
        )}
        <DialogFooter>
          {preview && <Button variant="outline" onClick={() => setPreview(null)}>Ganti Berkas</Button>}
          {preview && <Button onClick={commit} disabled={busy || preview.counts.ok + preview.counts.warning === 0} data-testid="coa-import-commit">Impor {preview.counts.ok + preview.counts.warning} Baris</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
